/**
 * JupiterSwapProvider - Jupiter Aggregator implementation
 *
 * Uses Jupiter's REST API for quote fetching and swap execution.
 * https://station.jup.ag/docs/apis/swap-api
 */

import { SwapProvider, SwapQuote, SwapRouteStep } from './SwapProvider.js';
import { Connection, VersionedTransaction, PublicKey } from '@solana/web3.js';

// Updated Jupiter API endpoints (v1 - as of October 2025)
// Free tier: lite-api.jup.ag (no API key required, lower rate limits)
// Paid tier: api.jup.ag (requires API key from portal.jup.ag)
const JUPITER_QUOTE_API = 'https://lite-api.jup.ag/swap/v1/quote';
const JUPITER_SWAP_API = 'https://lite-api.jup.ag/swap/v1/swap';
const JUPITER_SWAP_INSTRUCTIONS_API = 'https://lite-api.jup.ag/swap/v1/swap-instructions';

export class JupiterSwapProvider extends SwapProvider {
    constructor(config = {}) {
        super(config);
        this.name = 'jupiter';
        this.displayName = 'Jupiter';
        this.logo = 'https://jup.ag/svg/jupiter-logo.svg';

        // Configuration
        this.endpoint = config.endpoint || 'https://api.mainnet-beta.solana.com';
        this.connection = null;
        this.pollInterval = config.pollInterval || 3000; // 3 seconds between quotes

        // Fee configuration - feeAccount (simple, Jan 2025+) takes priority over referralAccount (legacy)
        this.feeAccount = config.feeAccount || null;
        this.referralAccount = config.referralAccount || null;
        this.feeBps = config.feeBps || 0;

        // API key for paid tier (optional)
        // Get from https://portal.jup.ag
        this.apiKey = config.apiKey || null;

        // Use paid API endpoint if API key is provided
        this.quoteApiUrl = this.apiKey
            ? 'https://api.jup.ag/swap/v1/quote'
            : JUPITER_QUOTE_API;
        this.swapApiUrl = this.apiKey
            ? 'https://api.jup.ag/swap/v1/swap'
            : JUPITER_SWAP_API;

        // Polling state
        this._pollTimer = null;
        this._lastParams = null;
    }

    /**
     * Build headers for API requests
     */
    _getHeaders() {
        const headers = {
            'Content-Type': 'application/json'
        };

        if (this.apiKey) {
            headers['x-api-key'] = this.apiKey;
        }

        return headers;
    }

    async initialize() {
        try {
            this._emitStatus('connecting', 'Initializing Jupiter...');

            this.connection = new Connection(this.endpoint, {
                commitment: 'confirmed',
                confirmTransactionInitialTimeout: 120000
            });

            this.isConnected = true;
            this._emitStatus('connected', 'Jupiter ready');
            return true;
        } catch (error) {
            this._emitError(error);
            this._emitStatus('error', error.message);
            return false;
        }
    }

    async disconnect() {
        this.stopQuoteStream();
        this.isConnected = false;
        this._emitStatus('disconnected', 'Jupiter disconnected');
    }

    /**
     * Build query parameters for Jupiter API
     */
    _buildQuoteParams(params) {
        const queryParams = new URLSearchParams({
            inputMint: params.inputMint,
            outputMint: params.outputMint,
            amount: params.amount.toString(),
            slippageBps: (params.slippageBps || 50).toString(),
            swapMode: params.swapMode || 'ExactIn',
        });

        // Optional parameters
        if (params.onlyDirectRoutes) {
            queryParams.set('onlyDirectRoutes', 'true');
        }

        if (params.maxAccounts) {
            queryParams.set('maxAccounts', params.maxAccounts.toString());
        }

        // Only add platform fee if we have a fee account configured (either type)
        if (this.feeBps > 0 && (this.feeAccount || this.referralAccount)) {
            queryParams.set('platformFeeBps', this.feeBps.toString());
        }

        // Exclude certain AMMs if specified
        if (params.excludeDexes && params.excludeDexes.length > 0) {
            queryParams.set('excludeDexes', params.excludeDexes.join(','));
        }

        // Include only certain AMMs
        if (params.dexes && params.dexes.length > 0) {
            queryParams.set('dexes', params.dexes.join(','));
        }

        return queryParams;
    }

    /**
     * Parse Jupiter route into SwapRouteStep array
     */
    _parseRoute(routePlan) {
        if (!routePlan) return [];

        return routePlan.map(step => new SwapRouteStep({
            ammKey: step.swapInfo?.ammKey,
            label: step.swapInfo?.label || 'Unknown',
            inputMint: step.swapInfo?.inputMint,
            outputMint: step.swapInfo?.outputMint,
            inputAmount: parseInt(step.swapInfo?.inAmount || 0),
            outputAmount: parseInt(step.swapInfo?.outAmount || 0),
            feeAmount: parseInt(step.swapInfo?.feeAmount || 0),
            feeMint: step.swapInfo?.feeMint
        }));
    }

    /**
     * Generate route label from route plan
     */
    _getRouteLabel(routePlan) {
        if (!routePlan || routePlan.length === 0) return 'Direct';
        return routePlan.map(step => step.swapInfo?.label || 'Unknown').join(' → ');
    }

    /**
     * Convert Jupiter response to SwapQuote
     */
    _parseQuote(jupiterQuote) {
        const route = this._parseRoute(jupiterQuote.routePlan);

        return new SwapQuote({
            provider: this.name,
            inputMint: jupiterQuote.inputMint,
            outputMint: jupiterQuote.outputMint,
            inputAmount: parseInt(jupiterQuote.inAmount),
            outputAmount: parseInt(jupiterQuote.outAmount),
            priceImpact: parseFloat(jupiterQuote.priceImpactPct || 0),
            slippageBps: jupiterQuote.slippageBps,
            route: route,
            transaction: null, // Transaction is fetched separately
            expiresAt: Date.now() + 30000, // Jupiter quotes valid ~30 seconds
            computeUnits: jupiterQuote.computedAutoSlippage,
            platformFee: jupiterQuote.platformFee ? {
                amount: parseInt(jupiterQuote.platformFee.amount),
                bps: jupiterQuote.platformFee.feeBps
            } : null,
            routeLabel: this._getRouteLabel(jupiterQuote.routePlan),
            raw: jupiterQuote
        });
    }

    async getQuote(params) {
        if (!this.isConnected) {
            throw new Error('Jupiter provider not initialized');
        }

        this._emitStatus('fetching', 'Fetching Jupiter quote...');

        try {
            const queryParams = this._buildQuoteParams(params);
            const url = `${this.quoteApiUrl}?${queryParams.toString()}`;

            const response = await fetch(url, {
                headers: this._getHeaders()
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Jupiter API error: ${errorText}`);
            }

            const data = await response.json();

            if (data.error) {
                throw new Error(data.error);
            }

            const quote = this._parseQuote(data);
            this._emitStatus('ready', 'Quote received');
            return [quote]; // Return array for consistency

        } catch (error) {
            this._emitError(error);
            this._emitStatus('error', error.message);
            throw error;
        }
    }

    /**
     * Start polling for quotes (Jupiter doesn't support streaming)
     */
    startQuoteStream(params) {
        if (this.isStreaming) {
            this.stopQuoteStream();
        }

        this._lastParams = params;
        this.isStreaming = true;
        this._emitStatus('streaming', 'Quote polling started');

        const poll = async () => {
            if (!this.isStreaming) return;

            try {
                const quotes = await this.getQuote(this._lastParams);
                this._emitQuote(quotes);
            } catch (error) {
                // Don't stop streaming on transient errors
                console.warn('Jupiter quote poll error:', error);
            }

            if (this.isStreaming) {
                this._pollTimer = setTimeout(poll, this.pollInterval);
            }
        };

        // Start immediately
        poll();
        return true;
    }

    stopQuoteStream() {
        if (this._pollTimer) {
            clearTimeout(this._pollTimer);
            this._pollTimer = null;
        }
        this.isStreaming = false;
        this._lastParams = null;
        this._emitStatus('stopped', 'Quote polling stopped');
    }

    /**
     * Update streaming parameters without restarting
     */
    updateStreamParams(params) {
        this._lastParams = { ...this._lastParams, ...params };
    }

    async executeSwap(quote, wallet) {
        // Handle Vue refs from solana-wallets-vue
        const publicKey = wallet.publicKey?.value || wallet.publicKey;
        const signTransaction = wallet.signTransaction?.value || wallet.signTransaction;

        if (!publicKey) {
            throw new Error('Wallet not connected');
        }

        this._emitStatus('swapping', 'Building swap transaction...');

        try {
            // Fetch swap transaction from Jupiter
            const swapRequestBody = {
                quoteResponse: quote.raw,
                userPublicKey: publicKey.toString(),
                wrapAndUnwrapSol: true,
                dynamicComputeUnitLimit: true,
                prioritizationFeeLamports: 'auto'
            };

            // Add fee account if configured (feeAccount takes priority over referralAccount)
            const feeAccountAddress = this.feeAccount || this.referralAccount;
            if (feeAccountAddress) {
                swapRequestBody.feeAccount = feeAccountAddress;
            }

            const swapResponse = await fetch(this.swapApiUrl, {
                method: 'POST',
                headers: this._getHeaders(),
                body: JSON.stringify(swapRequestBody)
            });

            if (!swapResponse.ok) {
                const errorText = await swapResponse.text();
                throw new Error(`Jupiter swap API error: ${errorText}`);
            }

            const { swapTransaction } = await swapResponse.json();

            // Deserialize and sign transaction
            const transactionBuffer = Buffer.from(swapTransaction, 'base64');
            const transaction = VersionedTransaction.deserialize(transactionBuffer);

            this._emitStatus('signing', 'Please sign the transaction...');

            // Sign with wallet (handle Vue refs)
            const signedTransaction = await signTransaction(transaction);

            this._emitStatus('sending', 'Sending transaction...');

            // Send transaction
            const rawTransaction = signedTransaction.serialize();
            const signature = await this.connection.sendRawTransaction(rawTransaction, {
                skipPreflight: true,
                maxRetries: 2
            });

            this._emitStatus('confirming', 'Confirming transaction...');

            // Confirm transaction
            const confirmation = await this.connection.confirmTransaction(signature, 'confirmed');

            if (confirmation.value.err) {
                throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
            }

            this._emitStatus('success', 'Swap completed!');

            return {
                success: true,
                signature,
                inputAmount: quote.inputAmount,
                outputAmount: quote.outputAmount
            };

        } catch (error) {
            this._emitError(error);
            this._emitStatus('error', error.message);
            throw error;
        }
    }

    supportsStreaming() {
        return false; // Jupiter uses polling, not true streaming
    }
}

export default JupiterSwapProvider;
